/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Field,
    Input,
    makeStyles,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    tokens
} from '@fluentui/react-components';
import { Add24Regular, DeleteRegular, Dismiss24Regular, Edit24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ProjektRechnungKontoItem } from '../../models/ProjektRechnungKontoItem';
import { DataService } from '../../services/dataService';

type SortDirection = 'asc' | 'desc' | null;
type SortColumn = keyof ProjektRechnungKontoItem | null;

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: tokens.spacingHorizontalL,
        gap: tokens.spacingVerticalL,
    },
    headerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: tokens.spacingHorizontalL,
    },
    filterContainer: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        alignItems: 'center',
        flex: 1,
    },
    searchInput: {
        flex: 1,
        maxWidth: '400px',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        minHeight: '300px',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        flex: 1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
    },
    tableRow: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    actionButtons: {
        display: 'flex',
        gap: tokens.spacingHorizontalXS,
    },
    colActions: {
        width: '100px',
        minWidth: '100px',
    },
    colId: {
        width: '80px',
        minWidth: '80px',
    },
    colIBAN: {
        width: '200px',
        minWidth: '150px',
    },
    colBIC: {
        width: '120px',
        minWidth: '100px',
    },
    colBlz: {
        width: '120px',
        minWidth: '100px',
    },
    colKonto: {
        width: '120px',
        minWidth: '100px',
    },
    colBankName: {
        width: '200px',
        minWidth: '150px',
    },
    // Drawer styles
    section: {
        marginBottom: tokens.spacingVerticalL,
    },
    sectionTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        marginBottom: tokens.spacingVerticalM,
        color: tokens.colorNeutralForeground1,
    },
    formGroup: {
        display: 'grid',
        gap: tokens.spacingVerticalM,
        marginBottom: tokens.spacingVerticalL,
    },
    fieldRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingHorizontalM,
    },
    fullWidth: {
        gridColumn: '1 / -1',
    },
    footerActions: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        justifyContent: 'flex-end',
        paddingTop: tokens.spacingVerticalL,
        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    },
});

interface DrawerState {
    isOpen: boolean;
    item: ProjektRechnungKontoItem | null;
    isEditMode: boolean;
}

const AccountsTab: React.FC = () => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();

    const [items, setItems] = useState<ProjektRechnungKontoItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [sortColumn, setSortColumn] = useState<SortColumn>('iban');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [drawerState, setDrawerState] = useState<DrawerState>({
        isOpen: false,
        item: null,
        isEditMode: false,
    });
    const [formData, setFormData] = useState<ProjektRechnungKontoItem>({
        id: 0,
        bankName: '',
        blz: '',
        kontoNr: '',
        bic: '',
        iban: '',
    });
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<ProjektRechnungKontoItem | null>(null);

    const dataService = React.useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch all items
    useEffect(() => {
        const fetchItems = async () => {
            if (!dataService) {
                return;
            }

            try {
                setIsLoading(true);
                const data = await dataService.getProjektRechnungKontoItems();
                console.log('Fetched accounts:', data);
                setItems(data || []);
            } catch (err) {
                console.error('Error loading accounts:', err);
                setItems([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchItems();
    }, [dataService]);

    // Filter items based on search text
    const filteredItems = useMemo(() => {
        let results = items;

        // Apply search filter
        if (filterText.trim()) {
            const searchLower = filterText.toLowerCase();
            results = results.filter((item) => {
                return (
                    item.iban?.toLowerCase().includes(searchLower) ||
                    item.bic?.toLowerCase().includes(searchLower) ||
                    item.kontoNr?.toLowerCase().includes(searchLower) ||
                    item.blz?.toLowerCase().includes(searchLower) ||
                    item.bankName?.toLowerCase().includes(searchLower)
                );
            });
        }

        // Apply sorting
        if (sortColumn) {
            results = [...results].sort((a, b) => {
                let aValue: any = a[sortColumn];
                let bValue: any = b[sortColumn];

                // Handle null/undefined
                if (aValue == null) aValue = '';
                if (bValue == null) bValue = '';

                // Convert to lowercase for string comparison
                if (typeof aValue === 'string') aValue = aValue.toLowerCase();
                if (typeof bValue === 'string') bValue = bValue.toLowerCase();

                // Comparison
                if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return results;
    }, [items, filterText, sortColumn, sortDirection]);

    const handleSort = useCallback((column: SortColumn) => {
        if (sortColumn === column) {
            // Toggle direction or clear sort
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortColumn('iban');
                setSortDirection('asc');
            }
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    }, [sortColumn, sortDirection]);

    const handleAddNew = () => {
        setFormData({
            id: 0,
            bankName: '',
            blz: '',
            kontoNr: '',
            bic: '',
            iban: '',
        });
        setDrawerState({
            isOpen: true,
            item: null,
            isEditMode: false,
        });
    };

    const handleView = (item: ProjektRechnungKontoItem) => {
        setFormData(item);
        setDrawerState({
            isOpen: true,
            item: item,
            isEditMode: false,
        });
    };

    const handleEdit = (item: ProjektRechnungKontoItem) => {
        setFormData(item);
        setDrawerState({
            isOpen: true,
            item: item,
            isEditMode: true,
        });
    };

    const handleRowDoubleClick = (item: ProjektRechnungKontoItem) => {
        handleView(item);
    };

    const handleDrawerClose = () => {
        setDrawerState({
            isOpen: false,
            item: null,
            isEditMode: false,
        });
        setFormData({
            id: 0,
            bankName: '',
            blz: '',
            kontoNr: '',
            bic: '',
            iban: '',
        });
    };

    const handleDrawerSave = async () => {
        if (!dataService) return;

        if (!formData.iban?.trim()) {
            alert('Please enter an IBAN for the account');
            return;
        }

        try {
            setIsSaving(true);

            if (drawerState.isEditMode && drawerState.item) {
                // Update existing
                await dataService.updateProjektRechnungKonto(formData);
                setItems((prevItems) =>
                    prevItems.map((p) => (p.id === formData.id ? formData : p))
                );
            } else {
                // Add new
                await dataService.addProjektRechnungKonto(formData);
                // Reload the list
                const data = await dataService.getProjektRechnungKontoItems();
                setItems(data || []);
            }

            handleDrawerClose();
        } catch (err) {
            console.error('Error saving account:', err);
            alert('Error saving account: ' + (err instanceof Error ? err.message : 'Unknown error'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteClick = (item: ProjektRechnungKontoItem) => {
        setItemToDelete(item);
        setShowDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (!dataService || !itemToDelete) return;

        try {
            setIsSaving(true);
            await dataService.deleteProjektRechnungKonto(itemToDelete.id);

            // Remove the item from the list
            setItems((prevItems) =>
                prevItems.filter((p) => p.id !== itemToDelete.id)
            );

            setShowDeleteDialog(false);
            setItemToDelete(null);
        } catch (err) {
            console.error('Error deleting account:', err);
            alert('Error deleting account: ' + (err instanceof Error ? err.message : 'Unknown error'));
        } finally {
            setIsSaving(false);
        }
    };

    const isFormValid = useCallback(() => {
        // All these fields are required
        if (!formData.bankName || formData.bankName.trim() === '') {
            return false;
        }

        if (!formData.iban || formData.iban.trim() === '') {
            return false;
        }

        if (!formData.bic || formData.bic.trim() === '') {
            return false;
        }

        if (!formData.blz || formData.blz.trim() === '') {
            return false;
        }

        if (!formData.kontoNr || formData.kontoNr.trim() === '') {
            return false;
        }

        return true;
    }, [formData]);

    return (
        <div className={styles.container}>
            {/* Header with Filter and Add Button */}
            <div className={styles.headerContainer}>
                <div className={styles.filterContainer}>
                    <Input
                        placeholder="Search by IBAN, BIC, account number, bank name..."
                        value={filterText}
                        onChange={(_, data) => setFilterText(data.value)}
                        className={styles.searchInput}
                    />
                </div>
                <Button
                    icon={<Add24Regular />}
                    appearance="primary"
                    onClick={handleAddNew}
                >
                    Add Account
                </Button>
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading accounts..." />
                </div>
            )}

            {/* Empty State */}
            {!isLoading && items.length === 0 && (
                <div className={styles.emptyState}>
                    No accounts found. Click "Add Account" to create one.
                </div>
            )}

            {/* Items Table */}
            {!isLoading && filteredItems.length > 0 && (
                <div className={styles.tableContainer}>
                    <Table size="small">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell className={styles.colActions}>
                                    Actions
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className={styles.colBankName}
                                    onClick={() => handleSort('bankName')}
                                >
                                    Bank Name
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className={styles.colIBAN}
                                    onClick={() => handleSort('iban')}
                                >
                                    IBAN
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className={styles.colBIC}
                                    onClick={() => handleSort('bic')}
                                >
                                    BIC
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className={styles.colBlz}
                                    onClick={() => handleSort('blz')}
                                >
                                    BLZ
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className={styles.colKonto}
                                    onClick={() => handleSort('kontoNr')}
                                >
                                    Account #
                                </TableHeaderCell>

                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredItems.map((item) => (
                                <TableRow
                                    key={item.id}
                                    className={styles.tableRow}
                                    onDoubleClick={() => handleRowDoubleClick(item)}
                                >
                                    <TableCell>
                                        <TableCellLayout>
                                            <div className={styles.actionButtons}>
                                                <Button
                                                    icon={<Edit24Regular />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEdit(item);
                                                    }}
                                                    appearance="subtle"
                                                    size="small"
                                                    title="Edit"
                                                />
                                                <Button
                                                    icon={<DeleteRegular />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteClick(item);
                                                    }}
                                                    appearance="subtle"
                                                    size="small"
                                                    title="Delete"
                                                />
                                            </div>
                                        </TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.bankName || '-'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.iban || '-'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.bic || '-'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.blz || '-'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.kontoNr || '-'}</TableCellLayout>
                                    </TableCell>

                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* No results after filtering */}
            {!isLoading && items.length > 0 && filteredItems.length === 0 && (
                <div className={styles.emptyState}>
                    No accounts match your search
                </div>
            )}

            {/* Drawer for Add/Edit */}
            <Drawer
                open={drawerState.isOpen}
                onOpenChange={(_, { open }) => {
                    if (!open) handleDrawerClose();
                }}
                size="medium"
                type="overlay"
                position="end"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="Close"
                                icon={<Dismiss24Regular />}
                                onClick={handleDrawerClose}
                            />
                        }
                    >
                        {drawerState.isEditMode ? 'Edit Account' : drawerState.item ? 'View Account' : 'Add Account'}
                    </DrawerHeaderTitle>
                </DrawerHeader>

                <DrawerBody>
                    <div className={styles.section}>
                        <div className={styles.formGroup}>
                            <Field label="Bank Name" required>
                                <Input
                                    value={formData.bankName || ''}
                                    onChange={(_, data) =>
                                        setFormData({ ...formData, bankName: data.value })
                                    }
                                    disabled={!drawerState.isEditMode && drawerState.item !== null}
                                    placeholder="Enter bank name"
                                />
                            </Field>
                            <Field label="IBAN" required>
                                <Input
                                    value={formData.iban || ''}
                                    onChange={(_, data) =>
                                        setFormData({ ...formData, iban: data.value })
                                    }
                                    disabled={!drawerState.isEditMode && drawerState.item !== null}
                                    placeholder="Enter IBAN"
                                />
                            </Field>
                            <div className={styles.fieldRow}>
                                <Field label="BIC" required>
                                    <Input
                                        value={formData.bic || ''}
                                        onChange={(_, data) =>
                                            setFormData({ ...formData, bic: data.value })
                                        }
                                        disabled={!drawerState.isEditMode && drawerState.item !== null}
                                        placeholder="Enter BIC"
                                    />
                                </Field>
                                <Field label="BLZ" required>
                                    <Input
                                        value={formData.blz || ''}
                                        onChange={(_, data) =>
                                            setFormData({ ...formData, blz: data.value })
                                        }
                                        disabled={!drawerState.isEditMode && drawerState.item !== null}
                                        placeholder="Enter BLZ"
                                    />
                                </Field>
                            </div>
                            <Field label="Account Number" required>
                                <Input
                                    value={formData.kontoNr || ''}
                                    onChange={(_, data) =>
                                        setFormData({ ...formData, kontoNr: data.value })
                                    }
                                    disabled={!drawerState.isEditMode && drawerState.item !== null}
                                    placeholder="Enter account number"
                                />
                            </Field>
                        </div>

                        {/* Footer Actions */}
                        <div className={styles.footerActions}>
                            <Button
                                appearance="secondary"
                                onClick={handleDrawerClose}
                                disabled={isSaving}
                            >
                                {drawerState.isEditMode || !drawerState.item ? 'Cancel' : 'Close'}
                            </Button>
                            {(drawerState.isEditMode || !drawerState.item) && (
                                <Button
                                    appearance="primary"
                                    onClick={handleDrawerSave}
                                    disabled={isSaving || !isFormValid()}
                                >
                                    {isSaving ? <Spinner size="tiny" /> : 'Save'}
                                </Button>
                            )}
                        </div>
                    </div>
                </DrawerBody>
            </Drawer>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={(_, data) => setShowDeleteDialog(data.open)}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>
                            Delete Account
                        </DialogTitle>
                        <DialogContent>
                            Are you sure you want to delete this account{' '}
                            <strong>{itemToDelete?.iban}</strong>?
                        </DialogContent>
                        <div
                            style={{
                                display: 'flex',
                                gap: tokens.spacingHorizontalM,
                                justifyContent: 'flex-end',
                                paddingTop: tokens.spacingVerticalL,
                            }}
                        >
                            <Button
                                appearance="secondary"
                                onClick={() => setShowDeleteDialog(false)}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                appearance="primary"
                                onClick={handleConfirmDelete}
                                disabled={isSaving}
                            >
                                {isSaving ? <Spinner size="tiny" /> : 'Delete'}
                            </Button>
                        </div>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

export default AccountsTab;
